import { findLeakStart, parseFunctionCallsFromText } from './parse-function-calls';

export interface LeakedToolCall {
    toolName: string;
    /** Stringified JSON arguments — the same wire format a native provider tool call uses. */
    input: string;
}

export interface LeakedToolCallExtraction {
    /**
     * The prose BEFORE the first `<function_calls>` block — the only part of the text worth
     * keeping. Everything after it is written by a model that believes its calls already ran:
     * fake "Done!" confirmations, hallucinated `<function_results>` blocks, invented ids. Once
     * the calls are converted to real tool calls, the model gets to write an informed reply
     * AFTER the actual results come back, replacing all of that speculation.
     */
    text: string;
    toolCalls: LeakedToolCall[];
}

/**
 * Converts tool calls leaked into plain response text as pseudo-XML
 * (`<function_calls><invoke name="x"><parameter name="y">…`) into the shape of REAL provider
 * tool calls, for a `wrapLanguageModel` middleware to substitute into the response content.
 * That hands the calls to the AI SDK core as if the provider had returned structured
 * `tool_calls` — so schema validation, execution, feeding results back to the model, and the
 * multi-step loop all work identically to native tool-calling, even on model/gateway
 * combinations that never return structured `tool_calls` at all.
 *
 * Returns `null` when the text contains no `<function_calls>` block (the common, native case —
 * caller leaves the response untouched). Argument values are kept as the raw XML strings; the
 * tool's own schema is responsible for coercing them into typed shape. (A forgiving repair pass
 * used to sit in front of it in the editor; it went with the local operations on 2026-08-19 — see
 * `validateForgiving`, which is now unwired and waiting for a server-side home.)
 */
export function extractLeakedToolCalls(
    text: string,
    knownTools: readonly string[] = []
): LeakedToolCallExtraction | null {
    // Detected by the leak's own shape rather than by `<function_calls>`: `claude-opus-5` emits the
    // `antml:`-namespaced form with no wrapper at all, and looking for the wrapper found nothing —
    // so the calls never ran and the raw markup went into the chat bubble.
    const firstBlockStart = findLeakStart(text);
    const calls = parseFunctionCallsFromText(text, knownTools);

    // No markup AND nothing recognizable — the ordinary case of a reply that is just prose.
    if (firstBlockStart === -1 && !calls.length) {
        return null;
    }

    // A truncated response (output-length limit hit mid-tag) can have an opening tag with no
    // parseable complete call — still truncate the text so the dangling fragment isn't shown or
    // stored, just with no calls to run.
    //
    // With no markup at all, the recognizer found the calls somewhere in the prose and there is no
    // single point to cut at; the whole text goes, since everything in it is the model narrating a
    // call it had not made yet.
    return {
        text: firstBlockStart === -1 ? '' : text.slice(0, firstBlockStart).trim(),
        toolCalls: calls.map((call) => ({ toolName: call.name, input: JSON.stringify(call.args) })),
    };
}
